import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useFriskOppHypotese, useHypoteseListe, useHypoteseOvergang } from '../api/useHypoteseBehandling.ts';
import { useHypoteseConfig, useHypoteser, type Hypothesis, type HypothesisSummary } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { useTilgang } from '../skall/tilgang.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Arbeidsflate, type Gruppe } from './Arbeidsflate.tsx';
import { Tekstknapp, Varsel } from './deler.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { navnAv, statusNavn, stor } from './lesning.ts';
import { erHLeserPluss, H_REGISTERET } from './rommet.ts';
import { kortDato, kortDatoAv } from './setning.ts';
import { useSporsmaalTilMeg } from '../api/useHypoteseKilder.ts';
import { useMoeteListe } from '../api/useMoeter.ts';
import { MoeteDetalj, MoeteRad } from './Moeter.tsx';

/**
 * «Til deg» (DD-01 presisert 2026-09-26, P2; `admin-hypoteser.jsx` SideTilDeg): det som er
 * tildelt den innloggede — vurderer, beslutningstaker, eier — og, for den som står på
 * beslutningstakerlista, «Klar for avgjørelse»: Vurderes uten ventende spørsmål til innsenderen
 * (`awaitingSubmitter = false`) som personen ikke selv har sendt inn. Det er en LESNING av data,
 * ikke en tilstand: `?status=vurderes`, filtrert her mot `config.deciders` (P2, avgjort
 * 2026-09-26). Ingen ny status, intet nytt felt.
 *
 * Gruppene i fast rekkefølge, og en hypotese står i den første den hører til. Et eierforslag
 * (`foreslaatt`) er ikke en rad: det er kortet øverst (M5c, `hypotese-innsending.jsx` SideTilDeg),
 * og raden kommer under «Du er eier» når eierskapet er tatt.
 */
export function grupperTilDeg(tildelt: HypothesisSummary[], klar: HypothesisSummary[], megId: string | undefined, foreslaatt: ReadonlySet<string> = new Set()): Gruppe[] {
  const sett = new Set<string>(foreslaatt);
  const ta = (rader: HypothesisSummary[]) => rader.filter((h) => (sett.has(h.id) ? false : (sett.add(h.id), true)));
  const grupper: Gruppe[] = [
    { navn: 'Du vurderer', rader: ta(tildelt.filter((h) => h.reviewer?.id === megId && (h.status === 'mottatt' || h.status === 'vurderes'))) },
    { navn: 'Klar for avgjørelse', rader: ta(klar.filter((h) => h.status === 'vurderes' && !h.awaitingSubmitter && h.submittedBy.id !== megId)) },
    { navn: 'Du er beslutningstaker', rader: ta(tildelt.filter((h) => h.decider?.id === megId)) },
    { navn: 'Du er eier', rader: ta(tildelt.filter((h) => h.owner?.id === megId)) },
    // H3.3: «Varsle meg» — et menneske har endret statusen siden du trykket «OK» (Kh9: lesing tar
    // den aldri ut). Aldri et tall på hvor mange andre som følger (Kh8).
    { navn: 'Du følger', rader: ta(tildelt.filter((h) => h.watchChangedAt !== undefined)) },
    // Tjeneren tar også med vurderes-rader der personen bare står på beslutningstakerlista
    // (assignedToMe, 2026-09-27). De hører til «Klar for avgjørelse» over — en som venter på
    // innsenderen står ikke her.
    { navn: 'Tildelt deg', rader: ta(tildelt.filter((h) => h.reviewer?.id === megId || h.owner?.id === megId || h.decider?.id === megId)) },
  ];
  return grupper.filter((g) => g.rader.length > 0);
}

/** Foreslått eier (kontrakten): `owner` er den innloggede, og `ownerAcceptedAt` er tom. Bare i valgt. */
export const erEierforslag = (h: Hypothesis, megId: string | undefined): boolean => megId !== undefined && h.status === 'valgt' && h.owner?.id === megId && !h.ownerAcceptedAt;

export function HTilDeg({ nr, moeteId = null }: { nr: number | null; /** H3.2 (W9): møtet beslutningstakeren skal bekrefte, i detaljen. */ moeteId?: string | null }) {
  const me = useMe();
  const config = useHypoteseConfig();
  const tilgang = useTilgang();
  const megId = me.data?.id;
  const beslutter = Boolean(megId && config.data?.deciders.some((d) => d.id === megId));
  const tildelt = useHypoteseListe({ assignedToMe: true });
  const vurderes = useHypoteseListe({ status: 'vurderes' }, beslutter);
  const tildelteRader = useMemo(() => tildelt.data?.pages.flatMap((p) => p.items) ?? [], [tildelt.data]);
  const klareRader = useMemo(() => (beslutter ? (vurderes.data?.pages.flatMap((p) => p.items) ?? []) : []), [beslutter, vurderes.data]);

  // Om eierskapet er tatt, står bare i detaljen (`ownerAcceptedAt`): de valgte du eier, hentes.
  // H3.1: radens tall for notisen («37 venter») er `evidenceReach`, som bare står i detaljen.
  const kandidater = useMemo(() => tildelteRader.filter((h) => (h.status === 'valgt' && megId !== undefined && h.owner?.id === megId) || h.conclusionNoticeDue).map((h) => h.id), [tildelteRader, megId]);
  const detaljer = useHypoteser(kandidater);
  const forslag = detaljer.map((d) => d.data).filter((h): h is Hypothesis => h !== undefined && erEierforslag(h, megId));
  const forslagIder = forslag.map((h) => h.id).join(',');
  const venterNoekkel = detaljer.map((d) => (d.data?.conclusionNoticeDue ? `${d.data.id}:${d.data.evidenceReach}` : '')).join(',');
  const venter = useMemo(() => Object.fromEntries(venterNoekkel.split(',').filter(Boolean).map((x) => [x.split(':')[0]!, Number(x.split(':')[1])])), [venterNoekkel]);
  const foreslaatt = useMemo(() => new Set(forslagIder ? forslagIder.split(',') : []), [forslagIder]);
  const grupper = useMemo(() => grupperTilDeg(tildelteRader, klareRader, megId, foreslaatt), [tildelteRader, klareRader, megId, foreslaatt]);

  const overgang = useHypoteseOvergang();
  const friskOpp = useFriskOppHypotese();
  const [avslaar, setAvslaar] = useState<Hypothesis | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  const [melding, setMelding] = useState<{ tekst: string; feil?: boolean; n: number } | null>(null);
  // Hvor fokus skal når det det sto på, forsvinner: raden under «Du er eier» etter «Ta eierskapet»,
  // sidens overskrift etter «Avslå».
  const [fokusTil, setFokusTil] = useState<number | 'liste' | null>(null);
  const teller = useRef(0);
  const meld = (tekst: string, erFeil = false) => setMelding({ tekst, feil: erFeil, n: ++teller.current });

  useEffect(() => {
    if (fokusTil === null) return;
    const maal = fokusTil === 'liste' ? document.getElementById('hb-liste-h') : document.querySelector<HTMLElement>(`[data-testid="hb-rad-${fokusTil}"]`);
    if (!maal) return;
    maal.focus();
    setFokusTil(null);
    // Raden kommer når lista er hentet på nytt: gruppene er det som endrer seg.
  }, [fokusTil, grupper]);

  const laster = tildelt.isPending || (beslutter && vurderes.isPending) || config.isPending || detaljer.some((d) => d.isPending);
  const feilet = tildelt.isError || (beslutter && vurderes.isError);

  function nektet(h: Hypothesis, e: unknown, hva: 'ta' | 'avslaa') {
    if (!erApiFeil(e)) return meld('Noe gikk galt. Prøv igjen.', true);
    if (erNettfeil(e)) return meld('Ikke sendt — ingen nett. Prøv igjen når nettet er tilbake.', true);
    // Eierskapet er tatt et annet sted, eller beslutningstakeren har gitt den videre: hent på nytt,
    // så kortet og lista viser det som faktisk gjelder.
    friskOpp(h.id, h.number);
    setFeil(
      hva === 'avslaa' && e.code === 'invalid_status_transition'
        ? `Du har alt tatt eierskapet til ${nummerTekst(h.number)}, så det kan ikke avslås her. Vil du ikke eie den, kan ${navnAv(h.decider)} gi den videre.`
        : e.brukertekst,
    );
  }

  async function taEierskapet(h: Hypothesis) {
    setFeil(null);
    try {
      await overgang.mutateAsync({ id: h.id, overgang: { handling: 'acceptOwnership' } });
      // Foreslått ordlyd: wireframen tegner bare at raden flytter til «Du er eier».
      meld(`Du er eier av ${nummerTekst(h.number)} nå.`);
      if (h.number !== undefined) setFokusTil(h.number);
    } catch (e) {
      nektet(h, e, 'ta');
    }
  }

  async function avslaa(h: Hypothesis) {
    setAvslaar(null);
    setFeil(null);
    try {
      await overgang.mutateAsync({ id: h.id, overgang: { handling: 'declineOwnership' } });
      // Foreslått ordlyd, med dialogens løfte: beslutningstakeren får beskjed.
      meld(`Du avslo eierskapet. ${stor(navnAv(h.decider))} får beskjed.`);
      setFokusTil('liste');
    } catch (e) {
      nektet(h, e, 'avslaa');
    }
  }

  // H3.3 (W10): spørsmålene om kildene den innloggede eier — radene, uten utkastene (Kh15).
  const sporsmaal = useSporsmaalTilMeg();
  const spRader = sporsmaal.data ?? [];
  // H3.2 (W9, P8): møtene du skal bekrefte, og de bekreftede du registrerte der en begrunnelse mangler.
  const moeter = useMoeteListe({ forMe: true });
  const aaBekrefte = (moeter.data ?? []).filter((m) => m.status === 'venter_bekreftelse' && m.decider.id === megId);
  const begrunnelser = (moeter.data ?? []).filter((m) => m.status === 'bekreftet' && m.reasonsMissing > 0);
  const moeteKort =
    aaBekrefte.length || begrunnelser.length ? (
      <ul className="hb-rader" aria-label="Møter">
        {aaBekrefte.length ? <li className="hb-gruppe">{`Møte å bekrefte · ${aaBekrefte.length}`}</li> : null}
        {aaBekrefte.map((m) => (
          <li key={m.id}>
            <MoeteRad m={m} valgt={m.id === moeteId} til={`/h/til-deg/moter/${m.id}`} testid={`til-deg-moete-${m.id}`} />
          </li>
        ))}
        {begrunnelser.length ? <li className="hb-gruppe">{`Begrunnelse mangler · ${begrunnelser.length}`}</li> : null}
        {begrunnelser.map((m) => (
          <li key={m.id}>
            <MoeteRad m={m} valgt={m.id === moeteId} til={erHLeserPluss(tilgang) ? `/h/moter/${m.id}` : `/h/til-deg/moter/${m.id}`} testid={`til-deg-moete-${m.id}`} />
          </li>
        ))}
      </ul>
    ) : null;
  const kort =
    forslag.length > 0 || feil || spRader.length > 0 || moeteKort ? (
      <>
        {moeteKort}
        {forslag.length > 0 || feil ? (
          <div className="ho-forslag-liste">
            {feil ? <Varsel>{feil}</Varsel> : null}
            {forslag.map((h) => (
              <Eierforslag key={h.id} h={h} opptatt={overgang.isPending} onTa={() => void taEierskapet(h)} onAvslaa={() => setAvslaar(h)} />
            ))}
          </div>
        ) : null}
        {spRader.length > 0 ? (
          <ul className="hb-rader" aria-label="Spørsmål om kildene dine">
            <li className="hb-gruppe">Spørsmål om kildene dine · {spRader.length}</li>
            {spRader.map((q) => (
              <li key={q.id}>
                <Link to={`/h/til-deg/sporsmal/${q.id}`} className="hb-rad" data-testid={`sporsmaal-rad-${q.id}`}>
                  <Ikon navn="help-circle-outline" className="hb-rad-ikon" />
                  <span className="hb-rad-tittel">
                    {navnAv(q.askedBy)} spør om {q.source.name.toLocaleLowerCase('nb')}
                  </span>
                  <span className="hb-rad-hoyre">svar innen {kortDato(q.answerBy)}</span>
                  <span className="hb-rad-meta">
                    <b className="hb-rad-status">Spørsmål</b>
                    <span className="hb-rad-ledd">
                      <span aria-hidden="true" className="hb-prikk">
                        ·
                      </span>
                      {q.source.name}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </>
    ) : null;

  // DD-19: tom er en invitasjon med tall som er sanne. Bidragsyteren får setningen fra
  // `hypotese-innsending.jsx` med veien til registeret; behandleren og leseren panelets.
  const bidragsyter = !erHLeserPluss(tilgang);
  const tom =
    nr === null && !laster && !feilet && grupper.length === 0 && !kort ? (
      <div className="hb-tom" data-testid="hb-tom">
        {bidragsyter ? (
          <>
            <h1 className="panel-h1">Ingenting til deg nå</h1>
            <p className="hb-dempet">
              Her står det du blir bedt om å eie eller avgjøre. Se hva kollegene tester i <Link to={H_REGISTERET.sti}>registeret</Link>.
            </p>
          </>
        ) : (
          <>
            <h1 className="panel-h1">Ingenting venter på deg</h1>
            <p className="hb-dempet">Når du blir vurderer, eier eller beslutningstaker på en hypotese, står den her.</p>
          </>
        )}
      </div>
    ) : null;

  return (
    <>
      <Arbeidsflate
        tittel="Til deg"
        base="/h/til-deg"
        nr={nr}
        kartTittel="Til deg"
        grupper={grupper}
        laster={laster}
        kort={kort}
        melding={melding}
        venter={venter}
        detalj={moeteId ? <MoeteDetalj key={moeteId} id={moeteId} tilbake={{ navn: 'Til deg', til: '/h/til-deg' }} /> : undefined}
        feil={
          feilet ? (
            <Varsel
              handling={
                <Tekstknapp
                  onClick={() => {
                    void tildelt.refetch();
                    if (beslutter) void vurderes.refetch();
                  }}
                >
                  Prøv igjen
                </Tekstknapp>
              }
            >
              Kunne ikke hente det som venter på deg. Ingenting er endret.
            </Varsel>
          ) : null
        }
        tom={tom}
      />
      {avslaar ? (
        // «Avslå eierskapet?» (wireframen): en dialog, fordi det går til en annen person. Primær, ikke
        // rød — ingenting fjernes; hypotesen står som Valgt til beslutningstakeren navngir en ny eier.
        // Ingen grunn: decline-ownership har ingen kropp.
        <Dialog tittelId="ho-avslaa-h" onLukk={() => setAvslaar(null)} testid="ho-avslaa-dialog">
          <h2 id="ho-avslaa-h" className="panel-h2">
            Avslå eierskapet?
          </h2>
          <p className="panel-tekst">{`${stor(navnAv(avslaar.decider))} får beskjed og velger en annen eier. ${nummerTekst(avslaar.number)} står som ${statusNavn(config.data, avslaar) || 'Valgt'} til noen har tatt den.`}</p>
          <div className="panel-handlinger">
            <Knapp label="Avslå eierskapet" primaer onClick={() => void avslaa(avslaar)} testid="ho-avslaa" />
            <Knapp label="Avbryt" kbd="Esc" fokusFørst onClick={() => setAvslaar(null)} />
          </div>
        </Dialog>
      ) : null}
    </>
  );
}

/**
 * Eierforslaget (M5c): hvem som foreslår, når, hva det betyr — og ÉN fylt knapp («Ta eierskapet»)
 * med «Avslå …» som tekstlenke. Datoen er da forslaget kom: valget, eller «Gi videre».
 */
function Eierforslag({ h, opptatt, onTa, onAvslaa }: { h: Hypothesis; opptatt: boolean; onTa: () => void; onAvslaa: () => void }) {
  const id = `ho-forslag-${h.id}`;
  const valgt = h.transitions.find((t) => t.toStatus === 'valgt' && t.fromStatus === 'vurderes');
  const foreslaatt = [...h.transitions].reverse().find((t) => (t.kind === 'overgang' && t.toStatus === 'valgt') || t.kind === 'gitt_videre');
  return (
    <section className="hm-kort ho-forslag" aria-labelledby={id} data-testid="ho-forslag">
      <div className="hm-kort-hode">
        <b id={id} className="hm-kort-tittel">
          <Ikon navn="person-outline" className="ho-forslag-ikon" />
          {`${stor(navnAv(h.decider))} foreslår deg som eier`}
        </b>
        {foreslaatt ? <span className="hb-liten">{kortDatoAv(foreslaatt.at)}</span> : null}
      </div>
      <p className="hb-tekst">{`«${h.title ?? ''}» ble valgt${valgt ? ` ${kortDatoAv(valgt.at)}` : ''}. Som eier starter du testen og skriver konklusjonen — ingen test starter før du har tatt det.`}</p>
      <div className="ho-forslag-knapper">
        <button type="button" className="panel-knapp ho-ta" aria-busy={opptatt || undefined} onClick={opptatt ? undefined : onTa}>
          <Ikon navn="checkmark-outline" />
          Ta eierskapet
        </button>
        <button type="button" className="panel-knapp-lenke" onClick={onAvslaa}>
          Avslå …
        </button>
      </div>
    </section>
  );
}
